import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { useToast } from '../../hooks/use-toast';
import { Radio, Save, Eye, EyeOff, Code, Music2 } from 'lucide-react';
import DOMPurify from 'dompurify';
import axios from 'axios';
import authService from '../../services/authService';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Safe embed preview using ref + DOMPurify DOM fragment (no dangerouslySetInnerHTML)
const RadioPreviewEmbed = ({ embedCode }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && embedCode) {
      const fragment = DOMPurify.sanitize(embedCode, {
        ADD_TAGS: ['iframe'],
        ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'src'],
        RETURN_DOM_FRAGMENT: true,
      });
      ref.current.textContent = '';
      ref.current.appendChild(fragment);
    }
  }, [embedCode]);
  return <div ref={ref} className="radio-preview-container" />;
};

export const RadioManager = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [settings, setSettings] = useState({
    station_name: 'DharmaR Radio',
    embed_code: '',
    is_active: false
  });

  const fetchSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/radio`);
      setSettings({
        station_name: response.data.station_name || 'DharmaR Radio',
        embed_code: response.data.embed_code || '',
        is_active: response.data.is_active || false
      });
    } catch (error) {
      console.error('Failed to fetch radio settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load radio settings',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const token = authService.getToken();
      await axios.put(`${API_URL}/api/radio`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: 'Success',
        description: 'Radio settings saved successfully'
      });
    } catch (error) {
      console.error('Failed to save radio settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save radio settings',
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-orange-500 to-green-500 flex items-center justify-center">
            <Radio className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Radio Settings</h2>
            <p className="text-gray-400 text-sm">Configure your live radio stream</p>
          </div>
        </div>
        <Button
          onClick={handleSave}
          disabled={isSaving}
          className="bg-gradient-to-r from-orange-500 to-green-500 hover:from-orange-600 hover:to-green-600"
        >
          {isSaving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Settings Card */}
        <Card className="bg-black/50 border-orange-500/20">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Music2 className="w-5 h-5 text-orange-400" />
              Station Settings
            </CardTitle>
            <CardDescription>Configure your radio station details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Active Toggle */}
            <div className="flex items-center justify-between p-4 rounded-xl bg-black/30 border border-orange-500/10">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${settings.is_active ? 'bg-green-500 animate-pulse' : 'bg-gray-500'}`}></div>
                <div>
                  <Label className="text-white font-medium">Radio Active</Label>
                  <p className="text-gray-400 text-xs mt-0.5">Show radio player in navigation</p>
                </div>
              </div>
              <Switch
                checked={settings.is_active}
                onCheckedChange={(checked) => handleChange('is_active', checked)}
                className="data-[state=checked]:bg-green-500"
              />
            </div>

            {/* Station Name */}
            <div className="space-y-2">
              <Label htmlFor="station_name" className="text-gray-300">Station Name</Label>
              <Input
                id="station_name"
                value={settings.station_name}
                onChange={(e) => handleChange('station_name', e.target.value)}
                placeholder="Enter station name"
                className="bg-black/30 border-orange-500/20 text-white focus:border-orange-500"
              />
            </div>

            {/* Embed Code */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="embed_code" className="text-gray-300 flex items-center gap-2">
                  <Code className="w-4 h-4" />
                  Embed Code
                </Label>
              </div>
              <textarea
                id="embed_code"
                value={settings.embed_code}
                onChange={(e) => handleChange('embed_code', e.target.value)}
                placeholder="Paste your radio player embed code here (iframe, script, etc.)"
                rows={8}
                className="w-full px-3 py-2 bg-black/30 border border-orange-500/20 rounded-lg text-white text-sm font-mono focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500/50 resize-none"
              />
              <p className="text-gray-500 text-xs">
                Paste the embed code from your radio streaming service (e.g., Mixcloud, SoundCloud, custom player)
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Preview Card */}
        <Card className="bg-black/50 border-orange-500/20">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <Eye className="w-5 h-5 text-green-400" />
                Live Preview
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPreview(!showPreview)}
                className="border-orange-500/30 text-orange-400 hover:bg-orange-500/10"
              >
                {showPreview ? (
                  <>
                    <EyeOff className="w-4 h-4 mr-2" />
                    Hide
                  </>
                ) : (
                  <>
                    <Eye className="w-4 h-4 mr-2" />
                    Show
                  </>
                )}
              </Button>
            </div>
            <CardDescription>Preview how the radio player will appear</CardDescription>
          </CardHeader>
          <CardContent>
            {showPreview ? (
              <div className="bg-black/80 rounded-xl border border-orange-500/20 overflow-hidden">
                {/* Preview Header */}
                <div className="bg-gradient-to-r from-orange-500/20 to-green-500/20 p-4 border-b border-orange-500/20">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-orange-500 to-green-500 flex items-center justify-center">
                      <Music2 className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-sm">{settings.station_name || 'DharmaR Radio'}</h3>
                      <p className="text-green-400 text-xs flex items-center gap-1">
                        <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                        Live
                      </p>
                    </div>
                  </div>
                </div>

                {/* Preview Content */}
                <div className="p-4">
                  {settings.embed_code ? (
                    <RadioPreviewEmbed embedCode={settings.embed_code} />
                  ) : (
                    <div className="text-center py-8 text-gray-400">
                      <Radio className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">Add embed code to see preview</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <EyeOff className="w-12 h-12 mb-3 opacity-50" />
                <p className="text-sm">Click "Show" to see preview</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Help Section */}
      <Card className="bg-black/30 border-orange-500/10">
        <CardContent className="py-4">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center flex-shrink-0">
              <Code className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <h4 className="text-white font-medium mb-1">How to get embed code</h4>
              <p className="text-gray-400 text-sm">
                Most radio streaming services provide an embed code. Look for "Share" or "Embed" options on your streaming platform. 
                Common services include Mixcloud, SoundCloud, Radio.co, Azuracast, and custom Icecast players.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <style>{`
        .radio-preview-container iframe {
          width: 100% !important;
          border-radius: 8px;
        }
      `}</style>
    </div>
  );
};

export default RadioManager;
